import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Carousel, CarouselResponsiveOption } from 'primereact/carousel';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText as PrimeInputText } from "primereact/inputtext";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';
import useToast from '../../components/common/useToast';
import { errorHandler } from '../../utils/HelperFunctions';
import { AxiosError } from "axios";
import apiFunctions from '../../services/api-service';
import { useNavigate } from 'react-router-dom';
import { ClockIcon } from "@heroicons/react/24/outline";

type StoryItem = {
    id: string;
    image: string;
    createddate: string;
    isSeen: boolean;
    timeAgo: string;
    viewsCount: number;
};

const StoryPage = () => {
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);

    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [load, setLoad] = useState(true);

    const { showToast }: any = useToast();
    const [dataLoad, setDataLoad] = useState(true);

    const initialized = useRef(false);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const [selectedStories, setSelectedStories] = useState<StoryItem[]>([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUsername, setSelectedUsername] = useState('');
    const [selectedProfileImage, setSelectedProfileImage] = useState('');

    const [data, setData] = useState([]);

    const [isDialogVisible, setDialogVisible] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [paused, setPaused] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const onImageClick = (rowData: any) => {
        setSelectedStories(rowData.stories || []);
        setSelectedUserId(rowData._id);
        setSelectedUsername(rowData.username);
        setSelectedProfileImage(rowData.profilePicture);
        setDialogVisible(true);
        setCurrentSlide(0);
    };

    const rowSkeletonTemplate = () => <Skeleton height="1.25rem" className="rounded" />;

    const imageSkeletonTemplate = () => (
        <div className='flex flex-row gap-2 items-center'>
            <Skeleton shape="circle" size="50px" />
            <Skeleton height="1.25rem" width="120px" className="rounded" />
        </div>
    );

    const imageBodyTemplate = (rowData: any) => {
        return (
            <>
                <div className='flex flex-row gap-2 items-center'>
                    <img
                        src={rowData.profilePicture}
                        alt="Preview"
                        style={{
                            width: '50px',
                            height: '50px',
                            cursor: 'pointer',
                            borderRadius: "50%",
                            padding: "4px",
                            border: "2px solid red"
                        }}
                        onClick={() => onImageClick(rowData)}
                    />
                    <span
                        className="cursor-pointer text-gray-800 hover:text-blue-600 hover:underline transition capitalize-first"
                        onClick={() => navigate(`/admin/profiles/view/${rowData._id}`)}
                    >
                        {rowData.username}
                    </span>
                </div>
            </>
        );
    };

    const responsiveOptions: CarouselResponsiveOption[] = [
        { breakpoint: '1400px', numVisible: 1, numScroll: 1 },
        { breakpoint: '1199px', numVisible: 1, numScroll: 1 },
        { breakpoint: '767px', numVisible: 1, numScroll: 1 },
        { breakpoint: '575px', numVisible: 1, numScroll: 1 },
    ];

    useEffect(() => {
        if (isDialogVisible && !paused && selectedStories.length > 0) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((prev) => {
                    const next = prev + 1;
                    if (next >= selectedStories.length) {
                        clearInterval(intervalRef.current!);
                        setDialogVisible(false);
                        return 0;
                    }
                    return next;
                });
            }, 3000); // 3 sec per story
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isDialogVisible, paused, selectedStories.length]);

    const handleImageClick = () => {
        setPaused(prev => !prev);
    };

    const storyTemplate = (story: any) => {
        return (
            <div
                className="relative w-full h-[200px] sm:h-[250px]"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                <img
                    src={story.image}
                    alt="story"
                    className="w-full h-full object-cover rounded-lg cursor-pointer"
                    onClick={handleImageClick}
                />

                {/* Delete button */}
                {/* <button
                    className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full"
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteStory(story.id);
                    }}
                >
                    ❌
                </button> */}
            </div>
        );
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
        } else {
            setName("");
            setLoad(false);
        }
    };

    const header = (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center table-header">
                {/* Title */}
                <div className="sm:col-span-3">
                    <h2 className="card-title m-0">Stories</h2>
                </div>

                {/* Search */}
                <div className="sm:col-span-9 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <div className="search-box w-full sm:w-auto flex items-center gap-2">
                            <span className="whitespace-nowrap hidden sm:inline">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-64"
                                placeholder='Search by name or city...'
                                onChange={getsearchdata}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text ml-2">entries - {options.totalRecords} Total</span>;
        },
    };

    const onSort = (e: any) => {
        setSortOrder(e.sortOrder);
        setSortField(e.sortField);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    async function getStoriesData() {
        try {
            let url;
            setDataLoad(true);
            if (name !== undefined && name !== null && name !== "" && name.trim().length > 1) {
                url = `/story/allmultistories/${skipData}/${itemsPerPage}?search=${encodeURIComponent(name.trim())}`;
            } else {
                url = `/story/allmultistories/${skipData}/${itemsPerPage}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsPerPage)
                        );
                        settotalPages(totalpage);
                        setData(response.data.data);
                        // setProfileData(CountryFactory.createFromJsonArray(response.data.data));
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getStoriesData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, name, sortField, sortOrder]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getStoriesData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div className='dashboard-margin'>
                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className='col-span-12'>
                        {isMobile ? (
                            <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                                <div className="p-3 pb-2">{header}</div>
                                <div className="flex flex-col gap-2 px-3 pb-3">
                                    {dataLoad ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                                {imageSkeletonTemplate()}
                                                <div className="flex flex-col gap-1 mt-2">
                                                    <Skeleton height="0.75rem" width="70%" className="rounded" />
                                                    <Skeleton height="0.75rem" width="50%" className="rounded" />
                                                    <Skeleton height="0.75rem" width="40%" className="rounded" />
                                                </div>
                                            </div>
                                        ))
                                    ) : data.length === 0 ? (
                                        <div style={{ textAlign: "center", padding: "64px 0" }}>
                                            <ClockIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No stories found</p>
                                        </div>
                                    ) : (
                                        data.map((rowData: any) => (
                                            <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                                {imageBodyTemplate(rowData)}
                                                <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                                    <div><span className="font-medium">Followers:</span> {rowData.followers} · <span className="font-medium">Following:</span> {rowData.following}</div>
                                                    <div><span className="font-medium">Posts:</span> {rowData.totalPosts}</div>
                                                    <div><span className="font-medium">City:</span> {rowData.city}</div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ) : (
                            <DataTable scrollable
                                scrollHeight="calc(100vh - 270px)"
                                style={{ minHeight: "calc(100vh - 195px)", background: "#fff" }}
                                header={header}
                                value={dataLoad ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `skeleton-${i}` })) : data}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSort={onSort}
                                resizableColumns
                                removableSort
                                rows={itemsPerPage}
                                emptyMessage={
                                    <div style={{ textAlign: "center", padding: "64px 0" }}>
                                        <ClockIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                        <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No stories found</p>
                                    </div>
                                }
                            >
                                <Column header="Name" body={dataLoad ? imageSkeletonTemplate : imageBodyTemplate}></Column>
                                <Column field="followers" header="Followers" sortable={!dataLoad} body={dataLoad ? rowSkeletonTemplate : undefined} />
                                <Column field="following" header="Following" sortable={!dataLoad} body={dataLoad ? rowSkeletonTemplate : undefined} />
                                <Column field="totalPosts" header="Post" sortable={!dataLoad} body={dataLoad ? rowSkeletonTemplate : undefined} />
                                <Column field="city" header="City" sortable={!dataLoad} body={dataLoad ? rowSkeletonTemplate : undefined} />
                                {/* <Column header="Action" className="action-column" frozen body={actionBodyTemplate} /> */}
                            </DataTable>
                        )}
                        {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                            {/* PrimeReact Paginator */}
                            <Paginator
                                template={paginatoreTemplate}
                                first={startIndex}
                                rows={itemsPerPage}
                                totalRecords={totalRecords}
                            />

                            {/* Custom Pagination Buttons */}
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <Button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                    disabled={currentPage === 0}
                                >
                                    <span className="sm:hidden">Prev</span>
                                    <span className="hidden sm:inline">Previous</span>
                                </Button>

                                <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                    <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                        Page {currentPage + 1} of {totalPages || 1}
                                    </span>
                                </Button>

                                <Button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                    disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>}
                    </div>
                </div>
            </div>

            <Dialog
                header="Stories"
                visible={isDialogVisible}
                style={{ width: '90%', maxWidth: '400px' }}
                onHide={() => {
                    setDialogVisible(false);
                    setPaused(false);
                    setCurrentSlide(0);
                    if (intervalRef.current) clearInterval(intervalRef.current);
                }}
            >
                {selectedStories.length > 0 && (
                    <div style={{ textAlign: 'center' }}>
                        <div className="flex flex-col items-center">
                            {/* Name and uploaded time */}
                            <div className="flex flex-row gap-2 items-center justify-between">
                                <div className='flex flex-col items-center'>
                                    <div className='flex flex-row gap-2 items-center'>
                                        <img src={selectedProfileImage} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%' }} />
                                        <h3
                                            className="cursor-pointer text-gray-800 hover:text-blue-600 hover:underline transition capitalize-first"
                                            onClick={() => navigate(`/admin/profiles/view/${selectedUserId}`)}
                                        >
                                            {selectedUsername}
                                        </h3>
                                    </div>
                                    <p className="text-sm text-gray-500 m-0">{selectedStories[currentSlide]?.timeAgo}</p>
                                    <p className='m-0'>{selectedStories[currentSlide]?.viewsCount ?? 0} views</p>
                                </div>
                            </div>

                            {/* Progress Indicators */}
                            <div style={{ display: 'flex', gap: '5px', marginBottom: '20px' }}>
                                {selectedStories.map((_, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            flex: 1,
                                            height: '4px',
                                            backgroundColor: index === currentSlide ? 'blue' : 'lightgray',
                                            transition: 'background-color 0.3s',
                                        }}
                                    ></div>
                                ))}
                            </div>

                            {/* Carousel */}
                            <Carousel
                                value={selectedStories}
                                numVisible={1}
                                numScroll={1}
                                responsiveOptions={responsiveOptions}
                                className="custom-carousel"
                                itemTemplate={storyTemplate}
                                onPageChange={(e) => setCurrentSlide(e.page)}
                                page={currentSlide}
                                showIndicators={selectedStories.length > 1}
                                showNavigators={selectedStories.length > 1}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default StoryPage;
